import { useTheme, Box, styled, Avatar, Palette, Theme } from "@mui/material";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { UploadFile } from "@mui/icons-material";
import { Link, Typography } from "@platform/design-system";

import CensusDropzoneAcceptedFile from "./census-dropzone-accepted-file";
import CensusDropzoneErrorFile from "./census-dropzone-error-file";
import {
  ALLOWED_FORMATS,
  CUSTOM_ACCEPTED_FORMATS,
  EXCEL_VALID_TYPE,
  MAX_SIZE,
} from "./constants";

import type { JSX } from "react";

const DropZoneBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(9, 0),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: theme.spacing(13),
  fontFamily: theme.typography.fontFamily,
  overflow: "hidden",
  position: "relative",
  border: `1px dashed ${theme.palette.dropzone.lineSoft}`,
}));

interface ICensusDropzone {
  file: File | null;
  fileError: FileRejection | null;
  onChange: (file: File | null) => void;
  onFileError: (error: FileRejection | null) => void;
}

const CensusDropzone = (props: ICensusDropzone): JSX.Element | null => {
  const { file, fileError, onChange, onFileError } = props;
  const theme = useTheme();

  const validateFile = (file: File) => {
    const { size, type } = file;
    if (size > MAX_SIZE) {
      return { message: "File size exceeded", code: ErrorCode.FileTooLarge };
    }

    if (!EXCEL_VALID_TYPE.includes(type)) {
      return {
        message: "File extension is not supported ",
        code: ErrorCode.FileInvalidType,
      };
    }

    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: CUSTOM_ACCEPTED_FORMATS,
    validator: validateFile,
    multiple: false,
    onDropAccepted(files) {
      onChange(files[0]);
      onFileError(null);
    },
    onDropRejected(files) {
      onFileError(files[0]);
      onChange(null);
    },
  });

  const renderDropzone = () => (
    <DropZoneBox {...getRootProps({ mt: theme.spacing(3) })}>
      <input {...getInputProps()} />
      <Avatar
        sx={({ palette }: { palette: Palette }) => ({
          bgcolor: palette.surfaces.secondary,
          marginBottom: 1,
        })}
      >
        <UploadFile color="brandPrimary" />
      </Avatar>
      <Typography>
        <strong>
          <Link component="span" underline="always">
            Click to upload
          </Link>{" "}
          or drag and drop
        </strong>
      </Typography>
      <Typography variant="body-medium" mt={1}>
        <strong>{ALLOWED_FORMATS}</strong>
      </Typography>
    </DropZoneBox>
  );

  const showDropzone = !file && !fileError;

  return (
    <Box px={2}>
      {showDropzone ? renderDropzone() : null}
      {file ? (
        <CensusDropzoneAcceptedFile
          file={file}
          onDelete={() => onChange(null)}
        />
      ) : null}
      {fileError ? <CensusDropzoneErrorFile rejectedFile={fileError} /> : null}
    </Box>
  );
};

export default CensusDropzone;
